/* eslint-disable @typescript-eslint/no-namespace -- InterfaceOperation is a deliberate
   namespace/type/const declaration-merging taxonomy (InterfaceOperation.Blue.Borrow,
   InterfaceOperation.isBlueOperation, …); unwinding it to ES modules is a cross-cutting
   refactor across its many consumers with no runtime change, out of scope here. */
import type { Address, MarketId, PeripheralBalanceType } from '@morpho-org/blue-sdk'

export namespace InterfaceOperation {
  export enum InterfaceOperationType {
    supply = 'Supply',
    withdraw = 'Withdraw',
    supplyCollateral = 'Supply_Collateral',
    withdrawCollateral = 'Withdraw_Collateral',
    borrow = 'Borrow',
    repay = 'Repay',
    supplyCollateralBorrow = 'Supply_Collateral_Borrow',
    withdrawCollateralRepay = 'Withdraw_Collateral_Repay',
    metaMorphoDeposit = 'MetaMorpho_Deposit',
    metaMorphoWithdraw = 'MetaMorpho_Withdraw',
    wrap = 'Wrap',
    toggleAutoDeleverage = 'Toggle_Auto_Deleverage',
    vaultV2Deposit = 'VaultV2_Deposit',
    vaultV2Withdraw = 'VaultV2_Withdraw',
    vaultV2InKindRedeem = 'VaultV2_In_Kind_Redeem',
    vaultV1MigrateToV2 = 'VaultV1_Migrate_To_V2'
  }

  interface BaseInterfaceOperation {
    id: string
    type: InterfaceOperationType
    options: {
      disabledPeripheralTokens?: Set<PeripheralBalanceType>
      isMax: InterfaceOperationType[]
      origin?: 'position-manager' | 'simple-action-panel'
    }
  }

  export namespace Blue {
    interface BaseBlueOperation extends BaseInterfaceOperation {
      marketId: MarketId
      assets: bigint
    }

    export interface SupplyCollateral extends BaseBlueOperation {
      type: InterfaceOperationType.supplyCollateral
    }

    export interface WithdrawCollateral extends BaseBlueOperation {
      type: InterfaceOperationType.withdrawCollateral
    }

    export interface Borrow extends BaseBlueOperation {
      type: InterfaceOperationType.borrow
      shares: bigint
    }

    export interface Repay extends BaseBlueOperation {
      type: InterfaceOperationType.repay
      shares: bigint
    }

    export interface SupplyCollateralBorrow extends BaseInterfaceOperation {
      marketId: MarketId
      type: InterfaceOperationType.supplyCollateralBorrow
      loanAssets: bigint
      loanShares: bigint
      collateralAssets: bigint
    }

    export interface WithdrawCollateralRepay extends BaseInterfaceOperation {
      marketId: MarketId
      type: InterfaceOperationType.withdrawCollateralRepay
      loanAssets: bigint
      loanShares: bigint
      collateralAssets: bigint
    }
  }
  export type Blue =
    | Blue.SupplyCollateral
    | Blue.WithdrawCollateral
    | Blue.Borrow
    | Blue.Repay
    | Blue.SupplyCollateralBorrow
    | Blue.WithdrawCollateralRepay

  export const isBlueOperation = (operation: InterfaceOperation): operation is Blue =>
    [
      InterfaceOperation.InterfaceOperationType.borrow,
      InterfaceOperation.InterfaceOperationType.supplyCollateral,
      InterfaceOperation.InterfaceOperationType.withdrawCollateral,
      InterfaceOperation.InterfaceOperationType.repay,
      InterfaceOperation.InterfaceOperationType.withdrawCollateralRepay,
      InterfaceOperation.InterfaceOperationType.supplyCollateralBorrow
    ].includes(operation.type)

  export namespace MetaMorpho {
    export interface BaseMetaMorphoOperation extends BaseInterfaceOperation {
      vaultAddress: Address
      assets: bigint
      shares: bigint
    }

    export interface Deposit extends BaseMetaMorphoOperation {
      type: InterfaceOperationType.metaMorphoDeposit
    }

    export interface Withdraw extends BaseMetaMorphoOperation {
      type: InterfaceOperationType.metaMorphoWithdraw
    }
  }

  export type MetaMorpho = MetaMorpho.Deposit | MetaMorpho.Withdraw

  export const isMetaMorphoOperation = (operation: InterfaceOperation): operation is MetaMorpho =>
    [
      InterfaceOperation.InterfaceOperationType.metaMorphoDeposit,
      InterfaceOperation.InterfaceOperationType.metaMorphoWithdraw
    ].includes(operation.type)

  export namespace MorphoLegacy {
    export interface Wrap extends BaseInterfaceOperation {
      type: InterfaceOperation.InterfaceOperationType.wrap
    }
  }

  export namespace AutoDeleverage {
    export interface Toggle extends BaseInterfaceOperation {
      type: InterfaceOperation.InterfaceOperationType.toggleAutoDeleverage
      marketId: MarketId
      toEnable: boolean
    }
  }
}

export type InterfaceOperation =
  | InterfaceOperation.Blue
  | InterfaceOperation.MetaMorpho
  | InterfaceOperation.MorphoLegacy.Wrap
  | InterfaceOperation.AutoDeleverage.Toggle
